import { useEffect } from 'react'
import ImageUploadButton from '@/shared/components/ImageUploadButton/ImageUploadButton'
import { useImageSlots } from '@/shared/store/useImageSlots'
import { deleteLocalAsset } from '../../assets/localAssets'
import { uploadAsset } from '../../assets/upload'
import { rememberLocalAsset } from '../../hooks/useLocalAssetUrls'
import { resolveEditorAssetUrl } from '../../utils/assets'

/** The crop rectangle for each shape a page photo can take. */
const STENCILS: Record<string, { width: number; height: number }> = {
  '1:1': { width: 320, height: 320 },
  '4:3': { width: 360, height: 270 },
  '16:9': { width: 400, height: 225 },
  '3:4': { width: 270, height: 360 },
  original: { width: 400, height: 225 }
}

/*
  What has been stored for each cropped blob, shared by every field.

  The crop slot outlives the component: a section's card re-rendered, a block
  taken away and added back, and the same blob came round to a fresh instance
  that had no memory of it, uploaded it a second time and left a copy in this
  browser nothing pointed at. Keyed by the blob itself, so a blob that is gone
  takes its entry with it.
*/
const stored = new WeakMap<Blob, Promise<string>>()

interface PhotoFieldProps {
  /** The reference stored in the document, or an empty string for no photo. */
  value: string
  onChange: (assetId: string) => void
  label: string
  /** Which shape the crop is held to. Matches the image element's own ratio. */
  ratio?: keyof typeof STENCILS
  /**
   * Distinguishes this field's crop from every other one on screen. The shared
   * crop modal is a singleton keyed by this, so two fields sharing an id would
   * hand each other's photo back.
   */
  slotId: string
}

/**
 * A photo, picked the way every other photo in this app is picked.
 *
 * The shared tile and the shared crop modal do the picking; this is what
 * happens afterwards. The cropped blob goes through `uploadAsset`, which stores
 * it in this browser until `menu_core_be` has a websites domain to put it in,
 * and the reference it hands back is what the document keeps.
 */
function PhotoField(props: PhotoFieldProps) {
  const { value, onChange, label, ratio = '16:9', slotId } = props

  const slot = useImageSlots(state => state.slots[slotId])
  const clearSlot = useImageSlots(state => state.clear)

  useEffect(() => {
    const blob = slot?.croppedBlob
    if (!blob) return undefined

    let pending = stored.get(blob)
    if (!pending) {
      const name = slot?.originalFile?.name ?? 'photo.webp'
      pending = uploadAsset(blob, name).then(reference => {
        rememberLocalAsset(reference, blob)
        return reference
      })
      // A failed store is forgotten, so the next look at the slot tries again.
      pending.catch(() => stored.delete(blob))
      stored.set(blob, pending)
    }

    let stale = false
    pending
      .then(reference => {
        if (!stale && reference !== value) onChange(reference)
      })
      .catch(() => {
        // Nothing is stored and the document keeps the photo it had. The tile
        // still shows the crop, which is the slot's own preview.
      })
    return () => {
      stale = true
    }
  }, [slot?.croppedBlob, slot?.originalFile, onChange, value])

  const stencil = STENCILS[ratio] ?? STENCILS['16:9']

  return (
    <ImageUploadButton
      imageShape="rectangle"
      label={label}
      slotId={slotId}
      imageUrl={value ? resolveEditorAssetUrl(value) : undefined}
      imageSize={{ width: stencil.width * 1.2, height: stencil.height * 1.2 }}
      stencilSize={stencil}
      onDelete={() => {
        // The picture goes with the reference. A photo nothing points at would
        // sit in this browser for good, since nothing else can reach it.
        deleteLocalAsset(value).catch(() => undefined)
        clearSlot(slotId)
        onChange('')
      }}
      isVertical={ratio === '3:4'}
    />
  )
}

export default PhotoField
